import { useEffect, useState, useMemo, useCallback } from "react";
import { getActivitySummary, listActivities } from "../lib/api";
import { ActivityCard } from "../components/ActivityCard";
import { ActivityTimeline } from "../components/ActivityTimeline";
import { UploadCloud, Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { distanceUnit, distanceValue, fmtDuration } from "../lib/format";
import { useAuth } from "../lib/auth";
import { activitiesForLocalMonth, activitiesInBucket, dedupeActivitiesById } from "../lib/timeline";
import { availableCanonicalSportFilters, canonicalSportForFilter, sportFilterLabel } from "../lib/sportNormalization";
import { toast } from "sonner";
import { compareActivitiesByStart } from "../lib/dateTime";
import { getMetricVisual } from "../lib/metricVisuals";
import { PageHeader } from "../components/PageHeader";
import { MetricCard } from "../components/layout/MetricCard";
import { SectionHeader } from "../components/layout/SectionHeader";
import { ErrorState } from "../components/layout/ErrorState";
import { EmptyState } from "../components/layout/EmptyState";
import { LoadingSkeleton } from "../components/layout/LoadingSkeleton";
import { CoachPendingActionNotice } from "@/components/coaching/CoachPendingActionNotice";
import { useCoachPendingAction } from "@/hooks/useCoachPendingAction";
import { fetchCurrentTrainingBlock } from "../api/trainingPlanApi";
import { OnboardingPopup } from "../components/OnboardingPopup";
import { onboardingAutoOpen, showOnboardingResume } from "../lib/onboarding";

export const Dashboard = ({ onUploadClick, refreshKey }) => {
  const { user, refresh } = useAuth();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [activitySignal, setActivitySignal] = useState("unknown");
  const [planSignal, setPlanSignal] = useState("unknown");
  const { primary: coachPendingAction } = useCoachPendingAction();
  const unitSystem = user?.unit_system || "metric";
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sport, setSport] = useState("all");
  const [selectedBucket, setSelectedBucket] = useState(null);

  const fetchActivities = () => {
    setLoading(true);
    setSummaryLoading(true);
    setLoadError(null);
    return Promise.all([listActivities(), getActivitySummary(sport)])
      .then(([data, summaryData]) => {
        setActivities(data || []);
        setSummary(summaryData || null);
      })
      .catch((err) => {
        setActivities([]);
        setSummary(null);
        if (err.isAuthError) {
          setLoadError("session");
          toast.error("Your session expired. Please sign in again.");
        } else if (!err.response) {
          setLoadError("network");
          toast.error("Could not reach the server. Is the backend running?");
        } else {
          setLoadError("server");
          toast.error("Failed to load activities. Try refreshing.");
        }
      })
      .finally(() => {
        setLoading(false);
        setSummaryLoading(false);
      });
  };

  useEffect(() => {
    if (onboardingAutoOpen(user, { activities: activitySignal, plan: planSignal })) {
      setOnboardingOpen(true);
    }
  }, [user, activitySignal, planSignal]);

  useEffect(() => {
    let cancelled = false;
    setPlanSignal("unknown");
    fetchCurrentTrainingBlock()
      .then((block) => { if (!cancelled) setPlanSignal(block ? "yes" : "no"); })
      .catch(() => { if (!cancelled) setPlanSignal("unknown"); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setActivitySignal("unknown");
    listActivities()
      .then((data) => {
        if (cancelled) return;
        const rows = data || [];
        setActivities(rows);
        setActivitySignal(rows.length > 0 ? "some" : "none");
      })
      .catch((err) => {
        if (cancelled) return;
        setActivities([]);
        if (err.isAuthError) {
          setLoadError("session");
        } else if (!err.response) {
          setLoadError("network");
        } else {
          setLoadError("server");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    getActivitySummary(sport)
      .then((data) => { if (!cancelled) setSummary(data || null); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, sport]);

  const sports = useMemo(() => availableCanonicalSportFilters(activities), [activities]);

  const dedupedActivities = useMemo(() => dedupeActivitiesById(activities), [activities]);

  const filtered = useMemo(() => {
    if (sport === "all") return dedupedActivities;
    return dedupedActivities.filter((a) => canonicalSportForFilter(a) === sport);
  }, [dedupedActivities, sport]);

  const handleBucketChange = useCallback((bucket) => {
    setSelectedBucket(bucket);
  }, []);

  const visibleActivities = useMemo(() => {
    const monthKey = selectedBucket?.key;
    const scoped = /^\d{4}-\d{2}$/.test(String(monthKey || ""))
      ? activitiesForLocalMonth(filtered, monthKey)
      : activitiesInBucket(filtered, selectedBucket);
    return [...scoped].sort(compareActivitiesByStart);
  }, [filtered, selectedBucket]);

  const totals = {
    dist: summary?.total_distance_m ?? 0,
    dur: summary?.moving_time_seconds ?? 0,
    cals: summary?.calories ?? 0,
    avgHr: summary?.avg_hr ?? null,
    count: summary?.count ?? 0,
  };
  const cardsLoading = loading || summaryLoading;

  const sportLabel = sportFilterLabel(sport);
  const distanceVisual = getMetricVisual("distance");
  const durationVisual = getMetricVisual("duration");
  const caloriesVisual = getMetricVisual("calories");
  const hrVisual = getMetricVisual("hr");
  const brandVisual = getMetricVisual("brand");
  const distDisplay = distanceValue(totals.dist, unitSystem);

  return (
    <div className="min-w-0">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        subtitle={
          sport === "all"
            ? "Recorded endurance activities across every sport. Every metric is measured data."
            : `Showing ${sportLabel.toLowerCase()} activities. Every metric is measured data.`
        }
      />

      {showOnboardingResume(user, { activities: activitySignal, plan: planSignal }, onboardingOpen) ? (
        <div className="mb-6">
          <button
            type="button"
            className="btn-primary-app px-4 py-3 text-xs tracking-[0.14em] uppercase min-h-[44px]"
            data-testid="onboarding-resume"
            onClick={() => setOnboardingOpen(true)}
          >
            Finish setup
          </button>
        </div>
      ) : null}

      <OnboardingPopup
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onUploadClick={onUploadClick}
        refresh={refresh}
      />

      {coachPendingAction && (
        <div className="mb-6">
          <CoachPendingActionNotice
            primary={coachPendingAction}
            testId="dashboard-coach-pending-action"
            compact
          />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5 mb-8">
        <MetricCard
          icon={brandVisual.icon}
          label="Activities"
          value={totals.count}
          loading={cardsLoading}
          accent={brandVisual.accent}
          soft={brandVisual.soft}
          className="shadow-app-sm"
          testId="dashboard-activities"
        />
        <MetricCard
          icon={distanceVisual.icon}
          label="Distance"
          value={distDisplay != null ? distDisplay.toFixed(2) : "—"}
          unit={distanceUnit(unitSystem)}
          loading={cardsLoading}
          accent={distanceVisual.accent}
          soft={distanceVisual.soft}
          className="shadow-app-sm"
          testId="dashboard-distance"
        />
        <MetricCard
          icon={durationVisual.icon}
          label="Moving Time"
          value={fmtDuration(totals.dur)}
          loading={cardsLoading}
          accent={durationVisual.accent}
          soft={durationVisual.soft}
          className="shadow-app-sm"
          testId="dashboard-moving-time"
        />
        <MetricCard
          icon={caloriesVisual.icon}
          label="Calories"
          value={totals.cals ? Math.round(totals.cals).toLocaleString() : "—"}
          unit={totals.cals ? "kcal" : undefined}
          loading={cardsLoading}
          accent={caloriesVisual.accent}
          soft={caloriesVisual.soft}
          className="shadow-app-sm"
          testId="dashboard-calories"
        />
        <MetricCard
          icon={hrVisual.icon}
          label="Average HR"
          value={totals.avgHr ? Math.round(totals.avgHr) : "—"}
          unit={totals.avgHr ? "bpm" : undefined}
          loading={cardsLoading}
          accent={hrVisual.accent}
          soft={hrVisual.soft}
          className="shadow-app-sm"
          testId="dashboard-average-hr"
        />
      </div>

      {!loading && !loadError && filtered.length > 0 && (
        <ActivityTimeline
          activities={filtered}
          onBucketChange={handleBucketChange}
          unitSystem={unitSystem}
        />
      )}

      <SectionHeader
        title={selectedBucket ? `Activities for ${selectedBucket.rangeLabel}` : "Recent activities"}
        description={
          selectedBucket
            ? `${visibleActivities.length} recorded ${visibleActivities.length === 1 ? "activity" : "activities"} in this period.`
            : "Filter by sport. Period follows the selected timeline bar."
        }
        testId={selectedBucket ? "activities-period-heading" : undefined}
        actions={
          <div className="flex items-center gap-2 min-w-0">
            <Filter className="w-4 h-4 text-dark-text-muted shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <Select value={sport} onValueChange={setSport}>
              <SelectTrigger
                className="w-[min(100%,12.5rem)] text-xs uppercase tracking-widest"
                data-testid="sport-filter-select"
                aria-label="Filter sport"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-dark-surface-1 border-dark-border">
                {sports.map((s) => (
                  <SelectItem key={s} value={s} className="text-xs uppercase tracking-widest">
                    {sportFilterLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-loading">
          {[...Array(4)].map((_, i) => (
            <LoadingSkeleton key={i} className="h-40 app-card" />
          ))}
        </div>
      ) : loadError ? (
        <ErrorState
          title="Could Not Load Activities"
          message={
            {
              session: "Your sign-in session expired or is invalid.",
              network: "Could not connect to the server. Make sure the backend is running on port 8000.",
              server: "Something went wrong while loading your activities.",
            }[loadError]
          }
          onRetry={fetchActivities}
          testId="load-error-state"
        />
      ) : activities.length === 0 ? (
        <EmptyState
          title="No Activities Yet"
          description="Drop a .FIT / .GPX / .TCX file exported from your device or platform to see rich analytics."
          icon={UploadCloud}
          testId="empty-state"
          action={
            <>
              {user?.email ? (
                <p className="text-xs text-dark-text-muted mb-4 tracking-wide">
                  Signed in as <span className="text-dark-text-secondary">{user.email}</span>
                </p>
              ) : null}
              <button
                type="button"
                onClick={onUploadClick}
                className="btn-primary-app px-6 py-3 text-xs tracking-[0.2em] uppercase min-h-[44px]"
                data-testid="empty-state-upload-button"
              >
                <UploadCloud className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                Upload First Activity
              </button>
            </>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          compact
          title={`No ${sportFilterLabel(sport)} activities`}
          description="Try a different sport filter or upload a matching activity."
          icon={Filter}
          testId="filter-empty-state"
        />
      ) : !selectedBucket ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-40 app-card animate-pulse" />
          ))}
        </div>
      ) : visibleActivities.length === 0 ? (
        <EmptyState
          compact
          title="No activities in this period"
          description="Select another bar on the timeline above."
          testId="period-empty-state"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="activities-grid" key={selectedBucket.key}>
          {visibleActivities.map((a, i) => (
            <ActivityCard key={a.id} activity={a} index={i} unitSystem={unitSystem} />
          ))}
        </div>
      )}
    </div>
  );
};
